import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Col, Row, Spinner } from 'reactstrap';
import { Chart as ChartJS, registerables } from 'chart.js';
import { AutoSizer, Table, Column } from 'react-virtualized';
import { FaCircle } from '../../../../node_modules/react-icons/fa';
import Title from '../../../sharedComponents/Title/Title';
import formatters from '../../../config/formatters';
import EmptyDataMessage from '../../../sharedComponents/ErrorMessages/EmptyDataMessage';
import { Theme } from '../../../config/constants';
import { isMobileOnly, isMobile } from 'react-device-detect';

ChartJS.register(...registerables);

const DISTRIBUTOR_BREAKDOWN_LIST = {
    'Manufacturer' : {
        label: 'Manufacturer Brand',
        color: Theme().colors.accent
    },
    'Distributor' : {
        label: 'Distributor Brand',
        color: 'rgb(49, 65, 117)'
    },
    'Customer' : {
        label: 'Customer Brand',
        color: 'rgb(73, 164, 218)'
    }
}

const DashboardDistributorBreakdownChart = () => {
    const distributorBreakdownTableData = useSelector(({dashboard}) => dashboard.distributorBreakdownData);
    const isLoading = useSelector(({dashboard}) => dashboard.distributorBreakdownChartIsLoading);
    const usaIncluded = useSelector(({ dashboard }) => dashboard.appliedUsaIncluded);
    const distributorBreakdownListValues = Object.values(DISTRIBUTOR_BREAKDOWN_LIST);
    const distributorBreakdownListKeys = Object.keys(DISTRIBUTOR_BREAKDOWN_LIST);
    const distributorBreakdownTableLabels = useMemo(
      () => distributorBreakdownListValues.map((item) => item.label),
      [distributorBreakdownListValues]
    );
    const { percentFormatter, usCurrencyFormatter } = formatters();
    const tableColors = useMemo(
      () => distributorBreakdownListValues.map((item) => item.color),
      [distributorBreakdownListValues]
    );
    const distributorBreakdownDatasetsData = useMemo(() => {
        if (!distributorBreakdownTableData?.length) return [];

        return distributorBreakdownListKeys.map((item) => {
            const distributorBreakdownItem  = distributorBreakdownTableData.find(
            (breakpoint) => breakpoint?.item_brand_owner === item
            );

            if (!distributorBreakdownItem ) return 0;

            return usaIncluded ? (distributorBreakdownItem.total_volume ?? 0) : (distributorBreakdownItem.sales_percent ?? 0);
        });
    }, [distributorBreakdownTableData, distributorBreakdownListKeys, usaIncluded ]);

    const getDistributorBreakdownObject = (cellData) => DISTRIBUTOR_BREAKDOWN_LIST[cellData] ? DISTRIBUTOR_BREAKDOWN_LIST[cellData] : undefined;

    // eslint-disable-next-line
    useEffect(() => {setupDataTables()}, [distributorBreakdownTableData, isLoading]);

    const setupDataTables = () => {
        if(!isLoading && (distributorBreakdownTableData?.length > 0)) {
            var distributorTableCanvas = document.getElementById("distributorTableCanvas");
            var distributorChart = ChartJS.getChart("distributorTableCanvas");
            if(distributorChart !== undefined) {
                distributorChart.destroy();
            }
            // eslint-disable-next-line
            var distributorTable = new ChartJS( distributorTableCanvas ,{
                type: 'doughnut',
                data: {
                    labels: distributorBreakdownTableLabels,
                    datasets: [
                        {
                            data: distributorBreakdownDatasetsData,
                            backgroundColor: tableColors
                        }
                    ]
                },
                options: tableOptions
            });
        }
    };

    const tableOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                display: false
            },
            y: {
                display: false
            }
        },
        plugins: {
            title: {
                display: false,
            },
            legend: {
                display: false,
            },
            tooltip: {
                enabled: true,
                xAlign: 'center',
                yAlign: 'center',
                    callbacks: {
                    label: (ctx) => {
                        const value = ctx?.raw ?? 0;
                        return usaIncluded ? `Total Volume: ${usCurrencyFormatter.format(value)}` : `Sales %: ${percentFormatter.format(value)}`;
                    }
                }
            },
        },
    }), [usaIncluded , percentFormatter, usCurrencyFormatter]);

    if(isLoading || (distributorBreakdownTableData?.length <= 0)) {
        return (
            <Col className='w-100 h-100' style={{minHeight: '315px'}}>
                <Title title='Brand Breakdown'/>
                <Row className="d-flex justify-content-center align-items-center" style={{height: '70%'}}>
                    <Col>
                        {isLoading &&
                        <div className="d-flex justify-content-center">
                            <Spinner color='beplBlue' animation="border" />
                        </div>
                        }
                        {!isLoading &&
                        <EmptyDataMessage />
                        }
                    </Col>
                </Row>
            </Col>
        );
    }
    else return (
        <Col className='w-100 h-100' style={{minHeight: '315px'}}>
            <Title title='Brand Breakdown' />
            <Row className="mb-3 mx-0" style={{minHeight: '200px'}}>
                <Col className= {(isMobile ? 'col-4 p-0 w-50' : ' p-0 w-50')} style={isMobileOnly ? {marginTop: '-50px'} : {}}>
                    <canvas id="distributorTableCanvas"/>
                </Col>
                <Col className="w-50 text-size-14 p-0" style={{marginTop: '6%'}}>
                    <AutoSizer>
                        {({width, height}) => (
                            <Table className="table brandBreakdownTable border-grayLight" id="brandBreakdownTable"
                                width={width}
                                height={height}
                                headerHeight={34.5}
                                rowHeight={38}
                                rowCount={distributorBreakdownTableData?.length}
                                rowGetter={({ index }) => distributorBreakdownTableData?.[index]}
                            >
                                <Column
                                label='Brand'
                                dataKey='item_brand_owner'
                                width={width * 0.425}
                                cellRenderer={({cellData, rowIndex}) => (<span><FaCircle className='w-fc my-auto' style={{color: getDistributorBreakdownObject(cellData)?.color}}/> {getDistributorBreakdownObject(cellData)?.label}</span>)}
                                />
                                {usaIncluded  && (
                                    <Column
                                    width={width * 0.4}
                                    label='Total Volume'
                                    dataKey='total_volume'
                                    cellRenderer={({cellData}) => usCurrencyFormatter.format(cellData)}
                                    />
                                )}
                                <Column
                                width={width * 0.175}
                                label='Sales %'
                                dataKey='sales_percent'
                                cellRenderer={({cellData}) => percentFormatter.format(cellData)}
                                />
                            </Table>
                        )}
                    </AutoSizer>
                </Col>
            </Row>
        </Col>
    );

}

export default DashboardDistributorBreakdownChart;
